import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";
import { PlayCircle, Loader2, Sparkles } from "lucide-react";

/**
 * `/demo` — shareable zero-friction entry point.
 *
 * Supports `?ref=CODE` for affiliate / referral attribution. Persists the
 * ref code to localStorage so it survives the demo-to-signup handoff.
 * Fires `POST /api/auth/demo-login` and drops the visitor into the Command
 * Center with a fully seeded workspace.
 */
export default function DemoLanding() {
  const nav = useNavigate();
  const { applySession } = useAuth();
  const [search] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading | error
  const [error, setError] = useState("");

  useEffect(() => {
    const ref = (search.get("ref") || "").trim().toUpperCase();
    if (ref) {
      // Persisted so /register can pick it up when the tester converts.
      localStorage.setItem("af_ref_code", ref);
      track("demo_landing_ref_captured", { ref });
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/auth/demo-login`, {
          ref: ref || null,
          source: "demo_landing",
        });
        if (cancelled) return;
        if (data?.access_token) {
          applySession(data.user, data.access_token);
        }
        track("demo_login_completed", {
          seeded: !!data?.demo_seeded,
          source: "demo_landing",
          ref: ref || null,
        });
        nav("/v3", { replace: true });
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Could not start the demo — please try again.");
        setStatus("error");
      }
    })();
    return () => { cancelled = true; };
  }, [search, applySession, nav]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-16"
      style={{
        background: "radial-gradient(ellipse at top, #FEF3C7 0%, #F7F3EA 55%, #F1EEE6 100%)",
      }}
      data-testid="demo-landing-page"
    >
      <div className="max-w-md w-full text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 border-2 border-amber-300 shadow-inner mb-6">
          <PlayCircle size={30} className="text-amber-700" />
        </div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-3 text-slate-900">
          Loading your demo…
        </h1>
        <p className="text-slate-600 text-base leading-relaxed mb-8">
          Spinning up a fully-loaded workspace with sample properties, cash buyers, and Jarvis AI.
          Zero signup, zero cost.
        </p>
        {status === "loading" && (
          <div className="inline-flex items-center gap-2 text-amber-800 font-semibold">
            <Loader2 size={18} className="animate-spin" />
            <span>Preparing your workspace</span>
          </div>
        )}
        {status === "error" && (
          <div
            className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-red-800 text-sm text-left"
            data-testid="demo-landing-error"
          >
            <div className="font-bold mb-1">Couldn&apos;t start the demo</div>
            <div className="text-red-700">{error}</div>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-red-600 text-white hover:bg-red-700"
            >
              Try again
            </button>
          </div>
        )}
        <div className="mt-12 inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white/70 backdrop-blur-sm border border-amber-200 text-amber-900 text-xs font-bold uppercase tracking-[0.14em]">
          <Sparkles size={12} className="text-amber-600" />
          First 100 Closers program · 40% off for life
        </div>
      </div>
    </div>
  );
}
